import type { InferOutput } from "valibot";
import { object, pipe } from "valibot";
import { initIntegrationDefinition } from "../Integration";
import type { IncompleteIntegrationDefinition } from "../types";
import { useTranslation } from "../../state/i18n";
import { MAX_CHARACTERS_MD } from "../../constants";
import ApiService from "../../services/ApiService";
import { createMemo, ErrorBoundary, Suspense } from "solid-js";
import { isEqual, isNil } from "lodash-es";
import { parseToRecipients } from "../integrationUtils";
import { requireRecipients } from "../../utils/validation/recipient";
import { Icon } from "../../components/icon/Icon";
import InlineMessage from "../../components/inlineMessage/InlineMessage";
import MixedInput from "../../components/inputs/MixedInput";
import { insertBetween } from "../../utils/arrayUtil";
import { parseStringTemplate, splitLiteralsOnComma, stringTemplateToString } from "../../utils/stringTemplateUtil";
import { metaVariableOption, toFieldValueOptions } from "../../utils/selectOptionsUtil";
import { useFormServices } from "../../services/formServices";
import { RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS } from "../../utils/fieldUtil";
import { RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES, toVariableValueOption } from "../../variables/variableUtil";
import { folderReference, formReference } from "../../utils/validation/idReferences";
import FormId from "../../state/FormId";
import FolderId from "../../state/FolderId";
import { fromLegacyPlatformIcon } from "../../utils/iconUtil";
import type { SelectOption } from "../../types/Menu";
import { SingleSelectInput } from "../../components/menu/Select";
import { createDynamicValueOptions } from "../../utils/menu/searchableMenuUtil";
import { integrationsIcons } from "../IntegrationIcons";
import { keyFromSelector } from "i18next";
import type { FieldType } from "../../types.ts";
import type { VariableType } from "../../variables/types.ts";
import type { MetaVariableId } from "../../metaVariables/metaVariable.ts";
import { stringTemplate } from "../../utils/validation/stringTemplate.ts";

const MESSAGE_ALLOWED_PLACEHOLDER_FIELDS: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "slider",
  "smiley",
  "stopwatch",
  "text",
  "textArea",
  "time",
  "timeDifference",
];

const MESSAGE_ALLOWED_PLACEHOLDER_VARIABLES: VariableType[] = ["string", "boolean", "number", "date", "datetime"];

const MESSAGE_ALLOWED_PLACEHOLDER_META_VARIABLES: MetaVariableId[] = [
  "info.userId",
  "meta.location.longitude",
  "meta.location.latitude",
  "meta.registrationDate",
  "meta.guid",
  "meta.taskId",
  "meta.serialNumber",
  "info.formName",
  "info.summary",
];

export type TaskIntegrationDto = {
  collectionId?: string;
  formId?: string;
  recipients?: string;
  dynamicRecipients?: string[];
  message?: string;
};

const Schema = pipe(
  object({
    folderId: folderReference(),
    formId: formReference(),
    recipients: requireRecipients(),
    message: stringTemplate(
      MESSAGE_ALLOWED_PLACEHOLDER_FIELDS,
      MESSAGE_ALLOWED_PLACEHOLDER_VARIABLES,
      MESSAGE_ALLOWED_PLACEHOLDER_META_VARIABLES,
      MAX_CHARACTERS_MD,
    ),
  }),
);

export type TaskIntegrationConfiguration = InferOutput<typeof Schema>;
const TaskIntegration: IncompleteIntegrationDefinition<TaskIntegrationConfiguration, TaskIntegrationDto> = {
  type: "task",
  legacy: {
    namespace: "com.moreapp",
    key: "task",
    version: 8,
  },
  schema: Schema,
  icon: integrationsIcons["task"],
  titleKey: keyFromSelector(($) => $.TASK_INTEGRATION_TITLE),
  descriptionKey: keyFromSelector(($) => $.TASK_INTEGRATION_DESCRIPTION),
  verifiable: true,
  defaultProperties: () => ({
    folderId: undefined,
    formId: undefined,
    recipients: [],
    message: parseStringTemplate("", [], []),
  }),
  propertyKeys: {
    folderId: keyFromSelector(($) => $.TASK_INTEGRATION_CONFIGURATION_FOLDER_ID_LABEL),
    formId: keyFromSelector(($) => $.TASK_INTEGRATION_CONFIGURATION_FORM_ID_LABEL),
    recipients: keyFromSelector(($) => $.TASK_INTEGRATION_CONFIGURATION_RECIPIENTS_LABEL),
    message: keyFromSelector(($) => $.TASK_INTEGRATION_CONFIGURATION_MESSAGE_LABEL),
  },
  fromDto: (configuration, fields, variables) => {
    const staticRecipients = isNil(configuration.recipients)
      ? []
      : parseToRecipients(configuration.recipients, variables);

    // Not all items in dynamic recipients are wrapped with '${...}', which is needed if we want to use 'parseStringTemplate' for parsing
    const dynamicRecipientsAsString = (configuration.dynamicRecipients ?? [])
      .map((item) => (!item.startsWith("${") ? `$\{${item}}` : item))
      .join("");
    const dynamicRecipients = parseStringTemplate(dynamicRecipientsAsString, fields, variables);

    return {
      folderId: !isNil(configuration.collectionId) ? FolderId.from(configuration.collectionId) : undefined,
      formId: !isNil(configuration.formId) ? FormId.from(configuration.formId) : undefined,
      message: parseStringTemplate(configuration.message, fields, variables),
      recipients: [...staticRecipients, ...dynamicRecipients],
    };
  },
  toDto: (configuration, fields, variables): TaskIntegrationDto => {
    const recipients = configuration.recipients
      .filter((r) => r.type === "literal")
      .map((r) => r.value)
      .join(",");
    const dynamicRecipients = configuration.recipients
      .filter((r) => r.type === "field" || r.type === "variable" || r.type === "metaVariable")
      .map((r) => r.value.toString());

    return {
      // Do not remove '?' for 'folderId' and 'formId' (can be undefined, but this is not reflected in the type due to our use of Valibot InferOutput )
      collectionId: configuration.folderId?.toString(),
      formId: configuration.formId?.toString(),
      recipients,
      dynamicRecipients,
      message: stringTemplateToString(configuration.message, fields, variables),
    };
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, variableService } = useFormServices();

    const folders = ApiService.getFolders();
    const formOptions = createMemo<Map<string, SelectOption<FormId>[]>>(() => {
      // The key for this map cannot be a `FolderId` because `Map` uses `===` equal checks which do not do what you expect
      // for `Objects`
      const forms = new Map<string, SelectOption<FormId>[]>();
      folders.data?.forEach((folder) => {
        forms.set(
          folder.id.toString(),
          folder.forms
            .filter((form) => ["ACTIVE", "HIDDEN"].includes(form.status))
            .map((form) => ({
              variant: "icon",
              value: form.id,
              label: form.meta.name,
              icon: {
                name: fromLegacyPlatformIcon(form.meta.icon, "document"),
                type: "solid",
                style: { color: form.meta.theme.color },
              },
              description: form.meta.description,
            })),
        );
      });

      return forms;
    });

    return (
      <>
        <ErrorBoundary
          fallback={<InlineMessage variant="danger" label={t(($) => $.TASK_INTEGRATION_CONFIGURATION_FOLDER_ERROR)} />}
        >
          <Suspense
            fallback={
              <div class="flex justify-center py-4">
                <Icon name="spinner" size="xl" type="solid" class="animate-spin" />
              </div>
            }
          >
            <SingleSelectInput
              label={t(props.propertyKeys.folderId)}
              value={props.integration.configuration.folderId}
              onSelect={(value) => {
                if (!isEqual(props.integration.configuration.folderId, value)) {
                  props.updateConfiguration("formId", null);
                }
                props.updateConfiguration("folderId", value);
              }}
              options={
                folders.data?.map((folder) => ({
                  variant: "icon",
                  value: folder.id,
                  label: folder.meta.name,
                  icon: { name: folder.meta.icon, type: "solid" },
                  description: folder.meta.description,
                })) ?? []
              }
              errorMessage={props.getError("folderId")}
              required
              info={{
                title: t(props.propertyKeys.folderId),
                alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                  property: props.propertyKeys.folderId,
                }),
                description: t(($) => $.TASK_INTEGRATION_CONFIGURATION_FOLDER_ID_DESCRIPTION),
              }}
              sameWidth
              onClear={() => {
                props.updateConfiguration("folderId", null);
                props.updateConfiguration("formId", null);
              }}
            />

            <SingleSelectInput
              label={t(props.propertyKeys.formId)}
              value={props.integration.configuration.formId}
              onSelect={(value) => props.updateConfiguration("formId", value)}
              onClear={() => props.updateConfiguration("formId", null)}
              options={formOptions().get(props.integration.configuration.folderId?.toString()) ?? []}
              errorMessage={props.getError("formId")}
              required
              info={{
                title: t(props.propertyKeys.formId),
                alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.formId }),
                description: t(($) => $.TASK_INTEGRATION_CONFIGURATION_FORM_ID_DESCRIPTION),
              }}
              sameWidth
            />
          </Suspense>
        </ErrorBoundary>

        <MixedInput
          label={t(props.propertyKeys.recipients)}
          value={insertBetween(props.integration.configuration.recipients, { type: "literal", value: ", " })}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={createDynamicValueOptions(t, {
            fields: fieldService
              .getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS)
              .flatMap((field) => toFieldValueOptions(field)),
            subforms: [],
            customVariables: variableService
              .getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES)
              .map(toVariableValueOption),
            metaVariables: [],
          })}
          onChange={(value) => {
            props.updateConfiguration("recipients", splitLiteralsOnComma(value));
          }}
          errorMessage={props.getError("recipients")}
          info={{
            title: t(props.propertyKeys.recipients),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.recipients }),
            description: t(($) => $.TASK_INTEGRATION_CONFIGURATION_RECIPIENTS_DESCRIPTION),
          }}
        />

        <MixedInput
          label={t(props.propertyKeys.message)}
          value={props.integration.configuration.message}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(MESSAGE_ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(MESSAGE_ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={createDynamicValueOptions(t, {
            fields: fieldService
              .getFieldsByTypes(MESSAGE_ALLOWED_PLACEHOLDER_FIELDS)
              .flatMap((field) => toFieldValueOptions(field)),
            subforms: [],
            customVariables: variableService
              .getVariablesByTypes(MESSAGE_ALLOWED_PLACEHOLDER_VARIABLES)
              .map(toVariableValueOption),
            metaVariables: MESSAGE_ALLOWED_PLACEHOLDER_META_VARIABLES.map(metaVariableOption),
          })}
          onChange={(value) => {
            props.updateConfiguration("message", value);
          }}
          errorMessage={props.getError("message")}
          required
          info={{
            title: t(props.propertyKeys.message),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.message }),
            description: t(($) => $.TASK_INTEGRATION_CONFIGURATION_MESSAGE_DESCRIPTION),
          }}
        />
      </>
    );
  },
};

export default initIntegrationDefinition(TaskIntegration);
